import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AdminHeader, Card, Btn, StatusBadge, Empty } from "@/components/admin/ui";
import { toast } from "sonner";
import { Star, Check, X, EyeOff, Eye } from "lucide-react";
import ReviewGallery from "@/components/storefront/ReviewGallery";

export default function AdminReviews() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/reviews").then(({ data }) => setItems(data.reviews));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    if (busy) return;
    setBusy(id);
    try {
      await api.put(`/admin/reviews/${id}`, { status });
      toast.success(`Review ${status}`);
      await load();
    } finally { setBusy(null); }
  };

  const toggleVisibility = async (r) => {
    if (busy) return;
    const hidden = !r.hidden;
    setBusy(r.id);
    try {
      await api.put(`/admin/reviews/${r.id}/visibility`, { hidden });
      toast.success(hidden ? "Review hidden" : "Review unhidden");
      await load();
    } finally { setBusy(null); }
  };

  return (
    <div>
      <AdminHeader title="Reviews" subtitle={`${items.length} reviews`} />
      <div className="space-y-3">
        {items.map((r) => (
          <Card key={r.id} className="p-4" data-testid={`review-${r.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-slate-900 text-sm">{r.productName}</p>
                  <StatusBadge status={r.status} />
                  {r.hidden && <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded" data-testid={`review-hidden-badge-${r.id}`}>Hidden</span>}
                  {r.verifiedPurchase && <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">Verified</span>}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-3.5 w-3.5 ${i < r.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}
                  <span className="text-xs text-slate-500 ml-1">by {r.userName}</span>
                </div>
                {r.title && <p className="font-medium text-sm text-slate-800 mt-1">{r.title}</p>}
                <p className="text-sm text-slate-600 mt-0.5">{r.comment}</p>
                <ReviewGallery images={r.images} size="sm" />
              </div>
              <div className="flex gap-2">
                <Btn variant="outline" onClick={() => setStatus(r.id, "approved")} disabled={busy === r.id} data-testid={`approve-${r.id}`}><Check className="h-4 w-4 text-emerald-600" /></Btn>
                <Btn variant="outline" onClick={() => setStatus(r.id, "rejected")} disabled={busy === r.id} data-testid={`reject-${r.id}`}><X className="h-4 w-4 text-red-600" /></Btn>
                <Btn variant="outline" onClick={() => toggleVisibility(r)} disabled={busy === r.id} data-testid={`toggle-visibility-${r.id}`} title={r.hidden ? "Unhide review" : "Hide review"}>
                  {r.hidden ? <Eye className="h-4 w-4 text-amber-600" /> : <EyeOff className="h-4 w-4 text-slate-500" />}
                </Btn>
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && <Empty text="No reviews yet" />}
      </div>
    </div>
  );
}
